import { useRef, useCallback } from 'react';

import {
  useBraintreeCreateMutation,
  BraintreeCreateRequest,
  BraintreeCreateResponse,
} from '@/api/payments/braintreeCreate';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { logger } from '@/services/logger/logger';
import { useClient } from '@/services/client/useClient';
import { useCartContext } from '@/services/cart/CartContext';
import { useSite, SiteData, SiteUrls } from '@/services/site/useSite';
import { PaymentSelectState } from '@/features/paymentSelect/paymentSelectSlice';
import { setTransactionId } from '@/features/paymentProcess/transactionSlice';
import { useJourneyOptions } from '@/services/journeys/useJourneyOptions';

const buildCreatePayload = (
  paymentSelectState: PaymentSelectState,
  paymentData: ApplePayJS.ApplePayPaymentAuthorizedEvent,
  client: string,
  siteData: SiteData,
  siteUrls: SiteUrls,
  campaignCode: string | null,
  isPayin: boolean,
  cartId?: string,
): BraintreeCreateRequest => ({
  client,
  order_reference: paymentSelectState.orderReference,
  amount: paymentSelectState.amount,
  currency: paymentSelectState.currency,
  giftaid: Number(paymentSelectState.giftaid),
  firstName: paymentData.payment.billingContact?.givenName ?? '',
  lastName: paymentData.payment.billingContact?.familyName ?? '',
  email: paymentData.payment.shippingContact?.emailAddress ?? '',
  address1: paymentData.payment.billingContact?.addressLines?.[0] ?? '',
  address2: paymentData.payment.billingContact?.addressLines?.[1] ?? '',
  address3: paymentData.payment.billingContact?.addressLines?.[2] ?? '',
  town: paymentData.payment.billingContact?.locality ?? '',
  postcode: paymentData.payment.billingContact?.postalCode ?? '',
  country: paymentData.payment.billingContact?.countryCode ?? '',
  campaign: campaignCode ?? siteData.campaign.name,
  transSource: siteData.campaign.transSource,
  transSourceUrl: siteUrls.transSourceUrl,
  transType: isPayin ? 'payin' : siteData.campaign.transType,
  paymentType: 'APPLEPAY',
  paymentMethod: paymentData.payment.token.paymentMethod,
  recaptcha_token: 'dummytoken',
  recaptcha_version: 3,
  cartId: cartId ?? siteData.defaultCart,
  ...(!!paymentSelectState.fixedTransactionId && { transactionId: paymentSelectState.fixedTransactionId }),
});

export function useBraintreeCreate() {
  const onSuccess = useRef<((data: BraintreeCreateResponse) => void) | undefined>(undefined);
  const onError = useRef<((error?: unknown) => void) | undefined>(undefined);
  const { cartId, getCartFinancialAttribution } = useCartContext();

  const dispatch = useAppDispatch();
  const paymentSelectState = useAppSelector(state => state.paymentSelect);

  const [braintreeCreateTrigger] = useBraintreeCreateMutation();

  const { getClientId } = useClient();
  const { getSiteData, getSiteUrls } = useSite();
  const { selectedJourney } = useJourneyOptions();
  const isPayin = selectedJourney === 'payin';

  // Trigger payment-service create
  const triggerCreateApi = useCallback(async (
    paymentData: ApplePayJS.ApplePayPaymentAuthorizedEvent,
    callbacks?: {
      onSuccess?: (data: BraintreeCreateResponse) => void,
      onError?: (error?: unknown) => void,
    }
  ) => {
    onSuccess.current = callbacks?.onSuccess;
    onError.current = callbacks?.onError;
    const payload = buildCreatePayload(
      paymentSelectState,
      paymentData,
      getClientId(),
      getSiteData(),
      getSiteUrls(),
      getCartFinancialAttribution()?.campaignCode ?? null,
      isPayin,
      cartId,
    );
    const result = await braintreeCreateTrigger(payload);

    // Handle payment-service create errors
    if (!!result?.error || result?.data?.message !== 'success' || !result?.data?.data?.transactionId) {
      logger.error('Error creating Braintree transaction');
      logger.error(result?.error);

      if (onError.current) {
        onError.current(result?.error);
      }
      return null;
    }

    // Dispatch payment-service create data to transactionState
    dispatch(setTransactionId(result?.data.data.transactionId));

    if (onSuccess.current) {
      onSuccess.current(result?.data);
    }

    return result?.data;
  }, [
    paymentSelectState,
    getClientId,
    getSiteData,
    getSiteUrls,
    getCartFinancialAttribution,
    braintreeCreateTrigger,
    dispatch,
    isPayin,
    cartId,
  ]);

  return {
    triggerCreateApi,
  };
}
